import { Field, Label, Textarea } from '@headlessui/react'
import clsx from 'clsx'
import Button from '../Button'
import { object, string } from 'yup'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import FormFieldError from '../errors/FormFieldError'
import PromptTestResult from './PromptTestResult'
import SimpleLoader from '../SimpleLoader'
import { REPLY_LLM_MODELS, DEFAULT_LLM_MODEL, getLlmModelLabel } from '../../models/LlmModel'
import InfoCallout from '../callout/InfoCallout'

// OpenAI GPT-5 models are reasoning models and don't support temperature
const OPENAI_MODEL_PREFIXES = ['gpt-5']

function isOpenAiModel(modelId: string): boolean {
    return OPENAI_MODEL_PREFIXES.some((prefix) => modelId.startsWith(prefix))
}

const PROMPT_MESSAGE_SUBSTITUTION_VARIABLE_REGEX = /<<<PLACEHOLDER MESSAGE>>>/

/**
 * Edit-and-test form for a profile's LLM reply prompt.
 *
 * This used to double as the editor for `LlmPrompt` classification rows, with
 * a text/image prompt-type switch and a prompt name. Classification prompts
 * are now authored as versioned ClassifierPrompts in the admin area, so the
 * classification-only controls were removed (EN-467) and the form is
 * reply-only.
 */
type Props = {
    prompt: string
    model?: string
    saving: boolean
    testing: boolean
    testResult?: string[] | number
    onSave: (prompt: string, model: string) => void
    onTest: (prompt: string, message: string, model: string, temperature?: number) => void
    onCancel?: () => void
    numberOfSuggestions?: number
    setNumberOfSuggestions?: (suggestions: number) => void
    temperature?: number
    setTemperature?: (temperature: number) => void
}

interface FormData {
    prompt: string
    model: string
    message?: string
}

const validationPromptSchema = object({
    prompt: string()
        .required('Please enter a prompt')
        .matches(
            PROMPT_MESSAGE_SUBSTITUTION_VARIABLE_REGEX,
            'Prompt must include the substitution variable <<<PLACEHOLDER MESSAGE>>>'
        ),
    model: string().required('Please select an LLM model'),
    // Don't define the validation here - we don't want this on a normal form submit, just when we click test.
    message: string()
})

export default function PromptDetailsForm(props: Props) {
    const { prompt, model, testing, testResult, onSave, onTest, onCancel } = props

    const promptForm = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationPromptSchema),
        defaultValues: {
            prompt: prompt,
            model: model || DEFAULT_LLM_MODEL,
            message: ``
        }
    })

    // Watch the model field to determine if temperature should be disabled
    const selectedModel = promptForm.watch('model')
    const isTemperatureDisabled = isOpenAiModel(selectedModel)

    const handleOnSubmitPrompt = async (data: FormData) => {
        onSave(data.prompt, data.model)
    }

    const handleOnTest = async () => {
        const prompt = promptForm.getValues().prompt
        const message = promptForm.getValues().message
        const model = promptForm.getValues().model
        // This is where we define form validation for the test message.
        if (!message || message === '') {
            promptForm.setError('message', {
                message: 'Please enter a message for testing.'
            })
        } else {
            onTest(prompt, message, model, props.temperature)
        }
    }

    return (
        <div>
            <FormProvider {...promptForm}>
                <form onSubmit={promptForm.handleSubmit(handleOnSubmitPrompt)}>
                    <Field className="mt-6">
                        <Label className="block font-medium leading-6 text-gray-90">Prompt</Label>

                        <Textarea
                            {...promptForm.register('prompt')}
                            className={clsx(
                                'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow',
                                'ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 ',
                                'focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6'
                            )}
                            rows={10}
                        />
                        {promptForm.formState.errors.prompt && (
                            <FormFieldError
                                message={promptForm.formState.errors.prompt.message || ''}
                            />
                        )}
                    </Field>
                    <Field className="mt-6">
                        <Label className="block font-medium leading-6 text-gray-90">
                            LLM Model
                        </Label>
                        <select
                            {...promptForm.register('model')}
                            className={clsx(
                                'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow',
                                'ring-1 ring-inset ring-gray-300',
                                'focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6'
                            )}
                        >
                            {REPLY_LLM_MODELS.map((llmModel) => (
                                <option
                                    key={llmModel.id}
                                    value={llmModel.id}
                                    selected={llmModel.isDefault}
                                >
                                    {getLlmModelLabel(llmModel)}
                                    {llmModel.isDefault ? ' (default)' : ''}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <div className="mt-6">
                        <Button
                            primary={true}
                            text="Save"
                            type="submit"
                            disabled={!promptForm.formState.isDirty}
                        ></Button>
                        <button
                            onClick={() => (onCancel ? onCancel() : promptForm.reset())}
                            className="py-2 px-6 underline self-end"
                            disabled={!promptForm.formState.isDirty}
                        >
                            Cancel
                        </button>
                    </div>
                    <div>
                        <Field className="mt-10">
                            <Label className="block font-medium leading-6 text-gray-90">
                                Test script
                            </Label>

                            <Textarea
                                {...promptForm.register('message')}
                                className={clsx(
                                    'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow',
                                    'ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 ',
                                    'focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6'
                                )}
                                rows={4}
                            />
                            {promptForm.formState.errors.message && (
                                <FormFieldError
                                    message={promptForm.formState.errors.message.message || ''}
                                />
                            )}
                        </Field>
                        {props.setNumberOfSuggestions ? (
                            <Field className="mt-10">
                                <Label className="block font-medium leading-6 text-gray-90">
                                    Number of suggestions
                                </Label>
                                <input
                                    type="number"
                                    name="number of suggestions"
                                    id="number of suggestions"
                                    className="w-full shadow focus:ring-primary-500 focus:border-primary block sm:text-sm border-gray-300 rounded-md mt-5"
                                    value={
                                        props.numberOfSuggestions ? props.numberOfSuggestions : 1
                                    }
                                    step="1"
                                    max={5}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        props.setNumberOfSuggestions
                                            ? props.setNumberOfSuggestions(
                                                  parseInt(event.target.value)
                                              )
                                            : console.log(
                                                  'Set number of suggestions not defined - this should never happen.'
                                              )
                                    }}
                                />
                            </Field>
                        ) : null}
                        {props.setTemperature ? (
                            <Field className="mt-6">
                                <Label className="block font-medium leading-6 text-gray-90">
                                    Temperature (0.0 - 1.0)
                                </Label>
                                {isTemperatureDisabled ? (
                                    <InfoCallout>
                                        Temperature is not supported for OpenAI GPT-5 models. These
                                        are reasoning models that use a different approach to
                                        control output variability.
                                    </InfoCallout>
                                ) : (
                                    <>
                                        <p className="text-sm text-gray-500 mb-2">
                                            Lower values produce more focused, deterministic
                                            responses. Higher values produce more creative, varied
                                            responses.
                                        </p>
                                        <input
                                            type="number"
                                            name="temperature"
                                            id="temperature"
                                            className="w-full shadow focus:ring-primary-500 focus:border-primary block sm:text-sm border-gray-300 rounded-md"
                                            value={props.temperature ?? 0.5}
                                            step="0.1"
                                            min={0}
                                            max={1}
                                            onChange={(
                                                event: React.ChangeEvent<HTMLInputElement>
                                            ) => {
                                                props.setTemperature?.(
                                                    parseFloat(event.target.value)
                                                )
                                            }}
                                        />
                                    </>
                                )}
                            </Field>
                        ) : null}
                        <div className="mt-2">
                            <Button primary={true} text="Test" onClick={handleOnTest}></Button>
                        </div>
                        {testing && <SimpleLoader loading={true} />}
                        {!testing && testResult !== undefined && (
                            <PromptTestResult result={testResult} />
                        )}{' '}
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}
